import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import LeagueDashboard from "@/pages/LeagueDashboard";
import AdminPanel from "@/pages/AdminPanel";
import DraftRoom from "@/pages/DraftRoom";
import TournamentSetup from "@/pages/TournamentSetup";
import SiteAdmin from "@/pages/SiteAdmin";
// iter87.78.iso — Rivalry Pro+ is now an isolated surface. Its own auth,
// session, and router entry — zero shared code with SiteAdmin.
// iter87.79 Phase A — Wrapped in <ClerkProvider> via ProShell so ONLY
// /rivalry-pro/* routes see Clerk state. Site Admin, Platform Admin,
// participant surfaces are physically outside the provider.
import RivalryProApp from "@/pro/RivalryProApp";
import RivalryProLogin from "@/pro/RivalryProLogin";
import RivalryProReset from "@/pro/RivalryProReset";
// iter87.199.69 — Pro+ variant of the shared /start onboarding flow.
import ProPlusNewRivalry from "@/pro/ProPlusNewRivalry";
// iter87.199.75 — Anon/authed router at /rivalry-pro index.
import ProPlusIndex from "@/pro/ProPlusIndex";
import { ProShell, ProClerkSignIn, ProClerkSignUp } from "@/pro/ProShell";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminAcceptInvite from "@/pages/AdminAcceptInvite";
import Recover from "@/pages/Recover";
import BillingSuccess from "@/pages/BillingSuccess";
import ProPlusBillingTerms from "@/pages/ProPlusBillingTerms";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import LeagueRecap from "@/pages/LeagueRecap";
// iter87.199.5 — CrownPoints page import removed with the Crown Race
// (formerly Rivalry Points) UI deprecation. The page component still
// lives on disk (`/pages/CrownPoints.jsx`) so we can re-enable the
// surface later without a data-migration; just re-import + re-add the
// `/league/:leagueId/crown-points` route.
import Hiatus from "@/pages/Hiatus";
import Register from "@/pages/Register";
import Start from "@/pages/Start";
import PlatformConsole from "@/pages/PlatformConsole";
import JoinPage from "@/pages/JoinPage";
import TesterApp from "@/pages/TesterApp";
import NotFound from "@/pages/NotFound";
import TenantBrandingProvider from "@/components/TenantBrandingProvider";
import TurnstileProvider from "@/components/TurnstileProvider";
import GlobalFooter from "@/components/GlobalFooter";
// iter87.199.22 — Temporary preview route for leaderboard restyle
// selection. Delete this import + route once the operator picks a
// variant and we wire it into `LeaderboardTable.jsx`.
import LeaderboardStylePreview from "@/pages/LeaderboardStylePreview";

function App() {
    return (
        <div className="App font-body">
            <TenantBrandingProvider />
            <TurnstileProvider />
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/admin" element={<SiteAdmin />} />
                    <Route path="/rivalry-pro" element={<ProShell />}>
                        <Route index element={<ProPlusIndex />} />
                        <Route path="login" element={<RivalryProLogin />} />
                        <Route path="sign-in/*" element={<ProClerkSignIn />} />
                        <Route path="sign-up/*" element={<ProClerkSignUp />} />
                        <Route path="reset/:token" element={<RivalryProReset />} />
                        {/* iter87.199.69 — Pro+ variant of /start.
                            Mounts inside ProShell so ClerkProvider is
                            available. Reuses the shared Start flow
                            with mode="pro_plus" + email locked to the
                            Clerk primary email. */}
                        <Route path="new" element={<ProPlusNewRivalry />} />
                    </Route>
                    <Route path="/admin/reset/:token" element={<AdminResetPassword />} />
                    <Route path="/admin/accept/:token" element={<AdminAcceptInvite />} />
                    <Route path="/recover/:token" element={<Recover />} />
                    {/* iter87.199.96 → iter87.199.137 — Paddle's
                        client-side checkout overlay lands here after a
                        successful subscription. Uses its own inline
                        ClerkProvider wrapper (see BillingSuccess.jsx) so
                        `useAuth()` can attach the bearer token to the
                        tier-poll request. Kept at the top level so it
                        doesn't need to be scoped inside /rivalry-pro. */}
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    {/* iter87.199.97 — Pro+ Billing Terms public page.
                        Linked from the Subscribe checkbox on the pro+ hub
                        and pending screen. */}
                    <Route path="/terms/pro-plus-billing" element={<ProPlusBillingTerms />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/league/:leagueId" element={<LeagueDashboard />} />
                    <Route path="/tester" element={<TesterApp />} />
                    <Route path="/league/:leagueId/recap" element={<LeagueRecap />} />
                    {/* iter87.199.5 — /league/:leagueId/crown-points route
                        retired with the Crown Race (formerly Rivalry
                        Points) UI deprecation. Backend tally endpoints
                        keep working so we can flip the surface back on
                        later without a migration. */}
                    <Route path="/league/:leagueId/setup" element={<TournamentSetup />} />
                    <Route path="/league/:leagueId/admin" element={<AdminPanel />} />
                    <Route path="/league/:leagueId/draft" element={<DraftRoom />} />
                    {/* iter69 — Tenant hiatus splash (suspended/archived) */}
                    <Route path="/hiatus" element={<Hiatus />} />
                    {/* iter71 — Dedicated registration page (moved from Landing card) */}
                    <Route path="/register" element={<Register />} />
                    <Route path="/start" element={<Start />} />
                    {/* iter80 — Platform Console (separate identity stack) */}
                    <Route path="/platform" element={<PlatformConsole />} />
                    <Route path="/platform/accept/:token" element={<PlatformConsole />} />
                    {/* iter80.2 — Platform password reset (reuses AdminResetPassword;
                        the backend response carries surface=platform so it redirects
                        back to /platform after a successful reset). */}
                    <Route path="/platform/reset/:token" element={<AdminResetPassword />} />
                    {/* iter84.15 — Magic-link claim for strict-mode invites */}
                    <Route path="/join/:token" element={<JoinPage />} />
                    {/* iter87.199.22 — Temporary preview route for the
                        leaderboard restyle. Delete after operator
                        picks a variant and it lands in
                        LeaderboardTable.jsx. */}
                    <Route path="/preview/leaderboard" element={<LeaderboardStylePreview />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                {/* iter87.199.19 — Global Tiny Crew Labs signature bar
                    inside BrowserRouter so it lives on every route and
                    below any per-page footer. */}
                <GlobalFooter />
            </BrowserRouter>
            <Toaster theme="dark" position="top-right" richColors closeButton />
        </div>
    );
}

export default App;
